import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, BarChart3 } from 'lucide-react';
import WidgetRenderer from '../../domains/dashboards/components/WidgetRenderer';

interface ManualChartBuilderProps {
  isOpen: boolean;
  widget: any;
  datasourceData: any[];
  onClose: () => void;
  onSave: (widgetId: string, config: any) => void;
}

const CHART_TYPES = [
  { type: 'bar', label: 'Barras', icon: '📊' },
  { type: 'line', label: 'Línea', icon: '📈' },
  { type: 'pie', label: 'Torta', icon: '🥧' },
  { type: 'metric', label: 'Métrica', icon: '🔢' },
];

const AGGREGATIONS = [
  { value: 'sum', label: 'Suma' },
  { value: 'count', label: 'Conteo' },
  { value: 'avg', label: 'Promedio' },
  { value: 'min', label: 'Mínimo' },
  { value: 'max', label: 'Máximo' },
];

const NUMBER_FORMATS = [
  { value: 'number', label: 'Número', example: '1234.56' },
  { value: 'currency', label: 'Moneda', example: '$1,234.56' },
  { value: 'percent', label: 'Porcentaje', example: '12.34%' },
  { value: 'compact', label: 'Compacto', example: '1.2K' },
];

const SORT_OPTIONS = [
  { value: 'value-desc', label: 'Mayor a menor' },
  { value: 'value-asc', label: 'Menor a mayor' },
  { value: 'label-asc', label: 'A-Z' },
  { value: 'label-desc', label: 'Z-A' },
];

const ManualChartBuilder: React.FC<ManualChartBuilderProps> = ({
  isOpen,
  widget,
  datasourceData,
  onClose,
  onSave,
}) => {
  const [chartType, setChartType] = useState('bar');
  const [xField, setXField] = useState('');
  const [yField, setYField] = useState('');
  const [legendField, setLegendField] = useState('');
  const [aggregation, setAggregation] = useState('sum');
  const [widgetTitle, setWidgetTitle] = useState('');
  
  // Filtros y formato
  const [topN, setTopN] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');
  const [sortBy, setSortBy] = useState('value-desc');
  const [numberFormat, setNumberFormat] = useState('number');
  const [showDataLabels, setShowDataLabels] = useState(false);

  // Detectar campos disponibles
  const availableFields = datasourceData && datasourceData.length > 0 
    ? Object.keys(datasourceData[0]) 
    : [];

  // Análisis simple de campos
  const analyzeField = (field: string) => {
    if (!datasourceData || datasourceData.length === 0) return 'unknown';
    const sample = datasourceData[0][field];
    if (typeof sample === 'number' || !isNaN(parseFloat(sample))) {
      return 'numeric';
    }
    return 'categorical';
  };

  const numericFields = availableFields.filter(f => analyzeField(f) === 'numeric');
  const categoricalFields = availableFields.filter(f => analyzeField(f) === 'categorical');

  useEffect(() => {
    if (isOpen) {
      setChartType('bar');
      setXField('');
      setYField('');
      setLegendField('');
      setAggregation('sum');
      setWidgetTitle('');
      setTopN(null);
      setFilterText('');
      setSortBy('value-desc');
      setNumberFormat('number');
      setShowDataLabels(false);
    }
  }, [isOpen]);

  const generatePreviewData = () => {
    if (!datasourceData || !xField || !yField) return [];

    // Si hay campo de leyenda (series), agrupar por X y Leyenda
    if (legendField) {
      const grouped: Record<string, Record<string, number[]>> = {};
      
      datasourceData.forEach((row: any) => {
        const xKey = row[xField];
        const legendKey = row[legendField];
        if (xKey !== undefined && xKey !== null && legendKey !== undefined) {
          if (!grouped[xKey]) grouped[xKey] = {};
          if (!grouped[xKey][legendKey]) grouped[xKey][legendKey] = [];
          const value = parseFloat(row[yField]) || 0;
          grouped[xKey][legendKey].push(value);
        }
      });

      // Construir array con series
      const result: any[] = [];
      Object.entries(grouped).forEach(([xKey, legendGroups]) => {
        const dataPoint: any = { name: xKey, [xField]: xKey };
        
        Object.entries(legendGroups).forEach(([legendKey, values]) => {
          let aggregatedValue = 0;
          switch (aggregation) {
            case 'sum': aggregatedValue = values.reduce((a, b) => a + b, 0); break;
            case 'count': aggregatedValue = values.length; break;
            case 'avg': aggregatedValue = values.reduce((a, b) => a + b, 0) / values.length; break;
            case 'min': aggregatedValue = Math.min(...values); break;
            case 'max': aggregatedValue = Math.max(...values); break;
          }
          dataPoint[legendKey] = aggregatedValue;
        });
        
        result.push(dataPoint);
      });

      // Aplicar filtro de texto
      let filtered = result;
      if (filterText.trim()) {
        filtered = result.filter(item => 
          item.name.toString().toLowerCase().includes(filterText.toLowerCase())
        );
      }

      // Ordenamiento
      filtered.sort((a, b) => {
        if (sortBy === 'label-asc') return a.name.toString().localeCompare(b.name.toString());
        if (sortBy === 'label-desc') return b.name.toString().localeCompare(a.name.toString());
        
        // Para value-asc/desc, sumar todos los valores de series
        const aTotal = Object.keys(a).filter(k => k !== 'name' && k !== xField).reduce((sum, k) => sum + (a[k] || 0), 0);
        const bTotal = Object.keys(b).filter(k => k !== 'name' && k !== xField).reduce((sum, k) => sum + (b[k] || 0), 0);
        
        return sortBy === 'value-desc' ? bTotal - aTotal : aTotal - bTotal;
      });

      // Top N
      if (topN && topN > 0) {
        filtered = filtered.slice(0, topN);
      }

      return filtered;
    }

    // Sin campo de leyenda (modo simple)
    const grouped: Record<string, number[]> = {};
    
    datasourceData.forEach((row: any) => {
      const key = row[xField];
      if (key !== undefined && key !== null) {
        if (!grouped[key]) grouped[key] = [];
        const value = parseFloat(row[yField]) || 0;
        grouped[key].push(value);
      }
    });

    let result = Object.entries(grouped).map(([key, values]) => {
      let aggregatedValue = 0;
      
      switch (aggregation) {
        case 'sum': aggregatedValue = values.reduce((a, b) => a + b, 0); break;
        case 'count': aggregatedValue = values.length; break;
        case 'avg': aggregatedValue = values.reduce((a, b) => a + b, 0) / values.length; break;
        case 'min': aggregatedValue = Math.min(...values); break;
        case 'max': aggregatedValue = Math.max(...values); break;
      }

      return {
        name: key,
        value: aggregatedValue,
        valor: aggregatedValue,
        [xField]: key,
        [yField]: aggregatedValue,
      };
    });

    // Aplicar filtro de texto
    if (filterText.trim()) {
      result = result.filter(item => 
        item.name.toString().toLowerCase().includes(filterText.toLowerCase())
      );
    }

    // Ordenamiento
    result.sort((a, b) => {
      if (sortBy === 'label-asc') return a.name.toString().localeCompare(b.name.toString());
      if (sortBy === 'label-desc') return b.name.toString().localeCompare(a.name.toString());
      if (sortBy === 'value-desc') return b.value - a.value;
      return a.value - b.value;
    });

    // Top N
    if (topN && topN > 0) {
      result = result.slice(0, topN);
    }

    return result;
  };

  const previewWidget = {
    id: 'preview',
    title: widgetTitle || 'Vista Previa',
    type: chartType,
    data: generatePreviewData(),
    config: {
      xField,
      yField,
      legendField,
      agregacion: aggregation,
      numberFormat,
      showDataLabels,
      sortBy,
      topN,
    },
  };

  const handleSave = () => {
    const datasourcePlaceholder = 'DATASOURCE_ID';
    let sqlQuery = '';
    
    if (chartType === 'metric') {
      sqlQuery = `SELECT ${aggregation.toUpperCase()}(${yField}) as valor FROM "${datasourcePlaceholder}"`;
    } else {
      const selectFields = legendField 
        ? `${xField}, ${legendField}, ${aggregation.toUpperCase()}(${yField}) as valor` 
        : `${xField}, ${aggregation.toUpperCase()}(${yField}) as valor`;
      const groupBy = legendField ? `${xField}, ${legendField}` : xField;
      const orderClause = sortBy.includes('value') ? 'valor' : xField;
      const orderDir = sortBy.includes('desc') ? 'DESC' : 'ASC';
      const limitClause = topN ? ` LIMIT ${topN}` : '';
      
      sqlQuery = `SELECT ${selectFields} FROM "${datasourcePlaceholder}" GROUP BY ${groupBy} ORDER BY ${orderClause} ${orderDir}${limitClause}`;
    }
    
    const newConfig = {
      title: widgetTitle || 'Nuevo Gráfico',
      type: chartType,
      config: {
        xField,
        yField,
        legendField,
        aggregation: aggregation,
        numberFormat,
        showDataLabels,
        sortBy,
        topN,
        filterText,
        sql: sqlQuery,
        datasourceReference: datasourcePlaceholder
      },
      allowedRoles: null
    };
    
    onSave('new', newConfig);
    onClose();
  };

  if (!isOpen) return null;

  const canSave = widgetTitle.trim() !== '' && xField !== '' && yField !== '';

  const modalContent = (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-slate-900 to-slate-950 rounded-2xl border border-slate-700/50 shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-sky-400" />
            Crear Gráfico
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* LEFT PANEL - Configuration */}
            <div className="space-y-4">
              
              {/* Tipo de Gráfico */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Seleccione el tipo de gráfico
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {CHART_TYPES.map((chart) => (
                    <button
                      key={chart.type}
                      onClick={() => setChartType(chart.type)}
                      className={`
                        p-3 rounded-lg border-2 transition-all text-center
                        ${chartType === chart.type
                          ? 'border-sky-500 bg-sky-500/20'
                          : 'border-slate-700 bg-slate-800/50 hover:border-sky-500/50'
                        }
                      `}
                    >
                      <div className="text-3xl mb-1">{chart.icon}</div>
                      <div className={`text-xs font-medium ${chartType === chart.type ? 'text-sky-400' : 'text-slate-300'}`}>
                        {chart.label}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Eje X */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Eje X
                </label>
                <select
                  value={xField}
                  onChange={(e) => setXField(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">Seleccionar campo...</option>
                  {categoricalFields.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>

              {/* Eje Y */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Eje Y (Valores)
                </label>
                <select
                  value={yField}
                  onChange={(e) => setYField(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">Seleccionar campo...</option>
                  {numericFields.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>

              {/* Series / Leyenda */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-2">
                  Series / Leyenda
                  <span className="text-slate-400 text-xs font-normal ml-2">(opcional)</span>
                </label>
                <p className="text-slate-400 text-xs mb-3">Divide los datos en múltiples series</p>
                <select
                  value={legendField}
                  onChange={(e) => setLegendField(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  <option value="">Sin series (datos simples)</option>
                  {categoricalFields.map((field) => (
                    <option key={field} value={field}>{field}</option>
                  ))}
                </select>
              </div>

              {/* Agregación */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Agregación
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {AGGREGATIONS.map((agg) => (
                    <button
                      key={agg.value}
                      onClick={() => setAggregation(agg.value)}
                      className={`
                        px-3 py-2 rounded-lg border transition-all text-xs font-medium
                        ${aggregation === agg.value
                          ? 'border-sky-500 bg-sky-500/20 text-white'
                          : 'border-slate-600 bg-slate-700 text-slate-300 hover:border-sky-500/50'
                        }
                      `}
                    >
                      {agg.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtros y Límites */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Filtros y Límites
                </label>
                
                <div className="space-y-3">
                  {/* Top N */}
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Top N elementos</label>
                    <input
                      type="number"
                      value={topN || ''}
                      onChange={(e) => setTopN(e.target.value ? parseInt(e.target.value) : null)}
                      placeholder="Todos"
                      min="1"
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>

                  {/* Búsqueda de texto */}
                  <div>
                    <label className="block text-slate-300 text-sm mb-2">Filtrar por texto</label>
                    <input
                      type="text"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      placeholder="Buscar..."
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>
              </div>

              {/* Ordenamiento */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Ordenamiento
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Formato */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Formato de Números
                </label>
                
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {NUMBER_FORMATS.map((fmt) => (
                    <button
                      key={fmt.value}
                      onClick={() => setNumberFormat(fmt.value)}
                      className={`
                        px-3 py-2 rounded-lg border transition-all text-xs
                        ${numberFormat === fmt.value
                          ? 'border-sky-500 bg-sky-500/20 text-white'
                          : 'border-slate-600 bg-slate-700 text-slate-300 hover:border-sky-500/50'
                        }
                      `}
                    >
                      <div className="font-medium">{fmt.label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{fmt.example}</div>
                    </button>
                  ))}
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showDataLabels}
                    onChange={(e) => setShowDataLabels(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-sky-500 focus:ring-2 focus:ring-sky-500"
                  />
                  Mostrar etiquetas de datos
                </label>
              </div>

              {/* Título del Widget */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <label className="block text-white font-semibold mb-3">
                  Título del Widget
                </label>
                <input
                  type="text"
                  value={widgetTitle}
                  onChange={(e) => setWidgetTitle(e.target.value)}
                  placeholder="Ingrese un título..."
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
                />
              </div>
            </div>

            {/* RIGHT PANEL - Preview */}
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
              <h3 className="text-white font-semibold mb-3">Vista Previa</h3>
              
              <div className="bg-slate-900/50 rounded-lg border border-slate-700 flex items-center justify-center" style={{ height: '500px' }}>
                {xField && yField ? (
                  <div className="w-full h-full p-4">
                    <WidgetRenderer widget={previewWidget} />
                  </div>
                ) : (
                  <div className="text-center text-slate-500">
                    <div className="text-6xl mb-4 opacity-20">📊</div>
                    <p className="font-medium">Configura los campos</p>
                    <p className="text-sm mt-2">Selecciona Eje X y Eje Y para ver el preview</p>
                  </div>
                )}
              </div>

              {xField && yField && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-400">{generatePreviewData().length} elementos</span>
                    <span className="text-sky-400 font-medium">{aggregation.toUpperCase()}</span>
                  </div>
                  
                  {/* Info de configuración aplicada */}
                  <div className="flex flex-wrap gap-2">
                    {legendField && (
                      <span className="px-2 py-1 bg-purple-500/20 border border-purple-500/40 rounded text-xs text-purple-300">
                        📊 Series: {legendField}
                      </span>
                    )}
                    {topN && (
                      <span className="px-2 py-1 bg-orange-500/20 border border-orange-500/40 rounded text-xs text-orange-300">
                        🔝 Top {topN}
                      </span>
                    )}
                    {filterText && (
                      <span className="px-2 py-1 bg-blue-500/20 border border-blue-500/40 rounded text-xs text-blue-300">
                        🔍 Filtro: "{filterText}"
                      </span>
                    )}
                    {numberFormat !== 'number' && (
                      <span className="px-2 py-1 bg-green-500/20 border border-green-500/40 rounded text-xs text-green-300">
                        💱 {NUMBER_FORMATS.find(f => f.value === numberFormat)?.label}
                      </span>
                    )}
                    {showDataLabels && (
                      <span className="px-2 py-1 bg-cyan-500/20 border border-cyan-500/40 rounded text-xs text-cyan-300">
                        🏷️ Etiquetas
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`px-6 py-2 rounded-lg transition-all font-medium flex items-center gap-2 ${
              canSave
                ? 'bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white shadow-lg shadow-sky-500/30'
                : 'bg-slate-700 text-slate-500 cursor-not-allowed'
            }`}
          >
            <span>✓</span>
            Aceptar
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ManualChartBuilder;